import _ from 'lodash';
import UUID from 'uuid';
import c from 'common/constants/flux-events';
import { GraphQLClient } from 'graphql-request';

import { createHttp, getNextBase } from 'utils/http';

// ------------
// Authentication
// ------------
export function init() {
    const http = createHttp();

    return async (dispatch, getState) => {
        dispatch({
            type: c.IMPERSONATION_MODAL__INIT,
        });

        const headers = {
            ...http._getHeaders(),

            // This is the whole reason for us skipping the HTTP module in favour of a one time exception.
            // We have to send a seperate auth token, and we don't anticipate having to do this anywhere else.
            Authorization: _.get(getState(), 'profile.impersonatorsToken.auth_token'),
        };
        const state = getState();

        try {
            let organizations;

            const graphqlUrl = getNextBase() + 'graphql';
            const client = new GraphQLClient(graphqlUrl, { headers });

            const query = `
            query getImpersonationOrgs($orgFilters: OrganizationFilters, $users: UserFilters) {
                organizations(filters: $orgFilters) {
                    id
                    name
                    type
                    client_type
                    parent {
                        id
                        name
                    }
                    users(filters: $users) {
                        id
                        name
                        first_name
                        last_name
                        email
                        global_role
                        organization
                        suspended
                        third_party
                    }
                }
            }`;

            const variables = {
                orgFilters: {
                    deprecated: false,
                    type: ['admin', 'media_buyer', 'co_managed_client', 'client'],
                },
                users: {
                    deprecated: false,
                    suspended: false,
                    third_party: false,
                },
            };

            const data = await client.request(query, variables);
            organizations = data.organizations;

            dispatch({
                type: c.IMPERSONATION_MODAL__INIT_SUCCESS,
                payload: {
                    organizations,
                    ownOrgId: _.get(state, 'profile.authToken.organization.id'),
                },
            });
        } catch (error) {
            dispatch({
                type: c.IMPERSONATION_MODAL__INIT_FAILURE,
                payload: {
                    error,
                },
            });

            console.error(error);
        }
    };
}

export function refreshUser(userId) {
    const http = createHttp();

    return async (dispatch, getState) => {
        dispatch({
            type: c.IMPERSONATION_MODAL__REFRESH_USER,
        });

        try {
            const user = await http.get(
                `users/${userId}`,
                {},
                {
                    headers: {
                        Authorization: _.get(getState(), 'profile.impersonatorsToken.auth_token'),
                    },
                }
            );
            dispatch({
                type: c.IMPERSONATION_MODAL__REFRESH_USER_SUCCESS,
                payload: {
                    user,
                },
            });
        } catch (error) {
            dispatch({
                type: c.IMPERSONATION_MODAL__REFRESH_USER_FAILURE,
                payload: {
                    error,
                },
            });

            console.error(error);
        }
    };
}

export function refreshOrganization(orgId) {
    const http = createHttp();

    return async (dispatch, getState) => {
        dispatch({
            type: c.IMPERSONATION_MODAL__REFRESH_ORGANIZATION,
        });

        try {
            const organization = await http.get(
                `organizations/${orgId}`,
                {},
                {
                    headers: {
                        Authorization: _.get(getState(), 'profile.impersonatorsToken.auth_token'),
                    },
                }
            );
            dispatch({
                type: c.IMPERSONATION_MODAL__REFRESH_ORGANIZATION_SUCCESS,
                payload: {
                    organization,
                },
            });
        } catch (error) {
            dispatch({
                type: c.IMPERSONATION_MODAL__REFRESH_ORGANIZATION_FAILURE,
                payload: {
                    error,
                },
            });

            console.error(error);
        }
    };
}

export function impersonate(userId) {
    return async (dispatch, getState) => {
        const state = getState();

        if (_.get(state, 'impersonate.isLoading')) {
            return;
        }

        const http = createHttp();

        if (_.get(state, 'profile.isImpersonating')) {
            const tokenId = _.get(state, 'profile.authToken.auth_token');
            if (tokenId) {
                try {
                    await http.del(`auth/${tokenId}`);
                } catch (err) {
                    // Keep existing impersonation UX even if token invalidation fails.
                    console.error('Failed to delete old impersonation token:', err);
                }
            }
        }

        dispatch({
            type: c.IMPERSONATION_MODAL__IMPERSONATION_START,
        });

        try {
            const token = await http.get(
                `auth/impersonate/${userId}`,
                {},
                {
                    headers: {
                        Authorization: _.get(getState(), 'profile.impersonatorsToken.auth_token'),
                    },
                }
            );

            const uuid = UUID.v4();

            // We don't want resources bleeding between impersonated sessions, just
            // in case it dirties up the impersonated user's view so we make sure to
            // clear session storage before an impersonation occurs.
            sessionStorage.clear();

            localStorage.setItem(uuid, JSON.stringify(token, null, 2));

            dispatch({
                type: c.IMPERSONATION_MODAL__IMPERSONATION_SUCCESS,
                payload: {
                    impersonatee: userId,
                },
            });

            // Preserve existing query params (e.g. search)
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.set('token', uuid);

            if (window.location.pathname.includes('/admin/')) {
                window.location = `/admin/${token.organization.id}?${searchParams.toString()}`;
            } else {
                window.location = `${window.location.pathname}?${searchParams.toString()}`;
            }
        } catch (error) {
            dispatch({
                type: c.IMPERSONATION_MODAL__IMPERSONATION_FAILURE,
                payload: {
                    errors: [error],
                },
            });
        }
    };
}

export function unimpersonate() {
    return (dispatch, getState) => {
        const http = createHttp();
        const tokenId = _.get(getState(), 'profile.authToken.auth_token');

        if (tokenId) {
            http.del(`auth/${tokenId}`).catch(() => {
                // Keep current redirect/session behavior even if token invalidation fails.
            });
        }

        dispatch({
            type: c.IMPERSONATION_MODAL__UNIMPERSONATE,
            payload: {},
        });
    };
}

export function setOrganization(id) {
    return {
        type: c.IMPERSONATION_MODAL__SET_ORG,
        payload: {
            id,
        },
    };
}

export function closeModal() {
    return {
        type: c.IMPERSONATION_MODAL__CLOSE,
        payload: {},
    };
}

export function openModal() {
    return {
        type: c.IMPERSONATION_MODAL__OPEN,
        payload: {},
    };
}
